import { useState, useCallback, useRef, useEffect } from 'react';
import { useGallery, useUpload, useDownload } from '../hooks/useGallery';
import { PhotoCard } from './PhotoCard';
import { Lightbox } from './Lightbox';
import { UploadZone } from './UploadZone';
import { classNames } from '../utils/helpers';

export function Gallery() {
  const {
    photos,
    pagination,
    albumName,
    privacyNotice,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useGallery({ limit: 50 });

  const { progress: uploadProgress, isUploading, upload, abort } = useUpload();
  const { isDownloading, downloadSelected, downloadAll } = useDownload();

  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement | null>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const sentinel = document.getElementById('gallery-sentinel');
    if (!sentinel) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [hasMore, isLoading, loadMore]);

  const toggleSelection = useCallback((id: number) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos.map(p => p.id)));
    }
  }, [photos, selectedPhotos.size]);

  const handleDownloadSelected = async () => {
    if (selectedPhotos.size === 0) return;
    await downloadSelected(Array.from(selectedPhotos));
    setSelectedPhotos(new Set());
    setSelectionMode(false);
  };

  const handleDownloadAll = async () => {
    await downloadAll();
  };

  const openLightbox = useCallback((index: number) => {
    setLastFocusedElement(document.activeElement as HTMLElement);
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = '';
    lastFocusedElement?.focus();
  }, [lastFocusedElement]);

  const handleKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
    if (lightboxIndex === null) return;
    if (e.key === 'Escape') closeLightbox();
    else if (e.key === 'ArrowLeft') setLightboxIndex((prev) => (prev !== null ? Math.max(0, prev - 1) : null));
    else if (e.key === 'ArrowRight') setLightboxIndex((prev) => (prev !== null ? Math.min(photos.length - 1, prev + 1) : null));
  }, [lightboxIndex, photos.length, closeLightbox]);

  useEffect(() => {
    if (lightboxIndex !== null) document.addEventListener('keydown', handleKeyDown as EventListener);
    return () => document.removeEventListener('keydown', handleKeyDown as EventListener);
  }, [lightboxIndex, handleKeyDown]);

  if (error) {
    return (
      <div className="gallery-error" role="alert">
        <div className="error-content">
          <svg className="error-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <h2>Fehler beim Laden</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={refresh}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery">
      <div className="gallery-header">
        <div className="gallery-title-section">
          <h1 className="gallery-title">{albumName}</h1>
          <p className="gallery-count">{pagination.total} Foto{pagination.total !== 1 ? 's' : ''}</p>
        </div>

        <div className="gallery-actions">
          <button
            className={classNames('btn btn-secondary btn-sm', { 'btn--active': selectionMode })}
            onClick={() => setSelectionMode(!selectionMode)}
            aria-pressed={selectionMode}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 11 12 14 22 4"></polyline>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            Auswahl
          </button>

          {selectionMode && (
            <button className="btn btn-secondary btn-sm" onClick={selectAll}>
              {selectedPhotos.size === photos.length ? 'Keine' : 'Alle'} auswählen
            </button>
          )}

          {selectionMode && selectedPhotos.size > 0 && (
            <button className="btn btn-primary btn-sm" onClick={handleDownloadSelected} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <span className="spinner spinner-sm"></span>
                  Herunterladen...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  {selectedPhotos.size} herunterladen
                </>
              )}
            </button>
          )}

          {!selectionMode && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={handleDownloadAll} disabled={isDownloading || photos.length === 0} title="Alle Fotos als ZIP herunterladen">
                {isDownloading ? <><span className="spinner spinner-sm"></span> ZIP...</> : 'Alle laden (ZIP)'}
              </button>
              <button className="btn btn-primary" onClick={() => setShowUploadZone(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Fotos hochladen
              </button>
            </>
          )}
        </div>
      </div>

      {privacyNotice && (
        <div className="privacy-notice" role="note">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
          <span>{privacyNotice}</span>
        </div>
      )}

      {isUploading && (
        <div className="upload-progress-bar" role="progressbar" aria-valuenow={uploadProgress.overallPercentage} aria-valuemin={0} aria-valuemax={100}>
          <div className="upload-progress-fill" style={{ width: `${uploadProgress.overallPercentage}%` }}></div>
          <span className="upload-progress-text">
            {uploadProgress.completedFiles} / {uploadProgress.totalFiles} &middot; {uploadProgress.overallPercentage}%
          </span>
          <button className="upload-progress-abort" onClick={abort} aria-label="Upload abbrechen">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}

      <div className="gallery-grid" ref={galleryRef} role="list" aria-label="Fotogalerie">
        {photos.length === 0 && !isLoading ? (
          <div className="gallery-empty" role="status">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <h2>Noch keine Fotos</h2>
            <p>Sei der Erste und lade Fotos hoch!</p>
            <button className="btn btn-primary" onClick={() => setShowUploadZone(true)}>
              Fotos hochladen
            </button>
          </div>
        ) : (
          <>
            {photos.map((photo, index) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                index={index}
                isSelected={selectedPhotos.has(photo.id)}
                selectionMode={selectionMode}
                onClick={() => {
                  if (selectionMode) toggleSelection(photo.id);
                  else openLightbox(index);
                }}
                onSelect={toggleSelection}
              />
            ))}

            {hasMore && (
              <div id="gallery-sentinel" aria-hidden="true">
                {isLoading && (
                  <div className="gallery-loading">
                    <div className="spinner"></div>
                    <span>Weitere Fotos laden...</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Lightbox
        photos={photos}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onNavigate={setLightboxIndex}
      />

      {showUploadZone && (
        <UploadZone
          onClose={() => setShowUploadZone(false)}
          onUpload={async (files, name) => {
            const res = await upload(files, name);
            refresh();
            return res;
          }}
        />
      )}
    </div>
  );
}